import React, { useRef } from 'react';

import { Link } from '@shared/core';
import { useOuterClick } from '@shared/utils/hooks/useOuterClick';
import { useRecalculateMenuPosition } from '@shared/utils/hooks/useRecalculateMenuPosition';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useKeycodeListener } from '@shared/utils/hooks/useKeycodeListener';
import { CloseButton, DialogV2, Divider, DrawerV2, Flex, TextV2, useDisclosure } from '@withjoy/joykit';
import { Moon } from '@withjoy/joykit/icons';

import { closeButtonOverrides, DialogBody, DialogHeader, drawerOverrides } from '@apps/admin/routes/Dashboard/common/commonDialog.styles';
import { ExpandArrow, ItemContent, ItemList, ListButton, ListButtonLabel, NavIcon, NavigationItemRoot, NavTitle, NewBadge, NewBadgeItem } from './ProductRowItem.styles';
import { ProductRowItemProps } from './ProductRowItem.types';
import { useTranslation } from '@shared/core';
import { TextEllipsisWrapper } from '@shared/utils/formatting/textEllipsis';

export const ProductRowItem: React.FC<ProductRowItemProps> = ({
  title,
  icon: Icon,
  onClick: onClickProp,
  hideDropdownLabel,
  path,
  pathIsInJoyWeb,
  subItems,
  hasNewPill,
  isDialogOnly,
  externalIsOpen,
  externalOnToggle,
  externalOnClose
}) => {
  const { t2 } = useTranslation('adminDashboardV2');
  const { newBadge } = t2('text');
  const navigationMenuRef = useRef<HTMLDivElement>(null);
  const hasSubItems = subItems && subItems.length > 0;
  const { isOpen: localIsOpen, onToggle: localOnToggle, onClose: localOnClose } = useDisclosure();
  const { isOpen, onToggle, onClose } =
    isDialogOnly && externalIsOpen && externalOnToggle && externalOnClose
      ? { isOpen: externalIsOpen, onToggle: externalOnToggle, onClose: externalOnClose }
      : { isOpen: localIsOpen, onToggle: localOnToggle, onClose: localOnClose };
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } });
  useKeycodeListener([27], onClose);

  const onClick = () => {
    onClickProp?.();
    recalculateMenuPosition();
    return hasSubItems ? onToggle() : null;
  };
  const menuRef = useOuterClick(onClick, !isMobile && isOpen);
  const drawerRef = useOuterClick(onClick, isMobile && isOpen);
  const { positionMenu, recalculateMenuPosition } = useRecalculateMenuPosition(navigationMenuRef, menuRef);

  const CONTENT = subItems?.map(child => {
    return (
      <div key={`${child.title}-${child.path}`}>
        {child.hasDivider && <Divider margin={'0.5rem'} width={'auto'} />}
        <ListButton as={child.pathIsInJoyWeb ? Link : 'a'} isOverflowOpen={isOpen} href={child.path} to={child.path} onClick={() => child.onClick?.()}>
          <Flex justifyContent="flex-start" alignItems="center" gap={5}>
            <ListButtonLabel>
              {child.icon && <child.icon size={'md'} />}
              <TextEllipsisWrapper>{child.title}</TextEllipsisWrapper>
            </ListButtonLabel>
            {child.hasNewPill && <NewBadge typographyVariant={'label3'}>{newBadge}</NewBadge>}
          </Flex>
          {child.isDisabled && <Moon size={'sm'} />}
        </ListButton>
      </div>
    );
  });

  if (isDialogOnly) {
    return (
      <DialogV2 isOpen={isOpen} onClose={onClose} size="md">
        <DialogHeader>
          <TextV2 typographyVariant="hed3" tagName={'h6'} padding={0}>
            {title}
          </TextV2>
          <CloseButton overrides={closeButtonOverrides} onClick={onClose} />
        </DialogHeader>
        <Divider />
        <DialogBody>{CONTENT}</DialogBody>
      </DialogV2>
    );
  }

  return (
    <>
      <NavigationItemRoot as={hasSubItems ? 'button' : pathIsInJoyWeb ? Link : 'a'} href={path} to={path} isOpen={isOpen} onClick={() => onClick()}>
        <ItemContent ref={menuRef} onClick={recalculateMenuPosition}>
          <NavIcon title={title}>
            {Icon && <Icon size={'xl'} />}
            {hasNewPill && <NewBadgeItem typographyVariant={'label3'}>{newBadge}</NewBadgeItem>}
          </NavIcon>
          <NavTitle typographyVariant="label2">
            {title}
            {hasSubItems && <ExpandArrow isOpen={isOpen} />}
          </NavTitle>
          {hasSubItems && (
            <ItemList isOpen={isOpen} ref={navigationMenuRef} position={positionMenu}>
              {!hideDropdownLabel && (
                <TextV2 typographyVariant="label3" tagName={'h6'} padding={'0.5rem'} textAlign={'left'}>
                  {title}
                </TextV2>
              )}
              {CONTENT}
            </ItemList>
          )}
        </ItemContent>
      </NavigationItemRoot>
      {isMobile && (
        <DrawerV2 disableAutoFocus enableReturnFocusOnClose useBackdrop={false} overrides={drawerOverrides} isOpen={isOpen} anchor="bottom">
          <div ref={drawerRef}>
            <DialogHeader>
              <TextV2 typographyVariant="hed3" tagName={'h6'} padding={0}>
                {title}
              </TextV2>
              <CloseButton overrides={closeButtonOverrides} onClick={() => onToggle()} />
            </DialogHeader>
            <Divider />
            <DialogBody>{CONTENT}</DialogBody>
          </div>
        </DrawerV2>
      )}
    </>
  );
};
